// Everything AHFC is owed, in one list — the Receivables tab of Claims.
//
// This used to be a separate page, "Gen Claims Tracker", with its own table
// (`claims`, never used: zero rows in its life), its own status vocabulary and
// its own denial-code list. Everything it showed came from the billing page,
// with `approved` relabelled "Submitted" — so in shadow mode every run looked
// filed. Now it reads claim_lines and dme_orders through lib/receivables.js,
// the same mapping the Finance overview uses, and a claim filed outside CAERA
// is recorded as an ordinary claim line with source = 'manual' so aging,
// remittance matching and denials see it like any other.
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import { format, startOfMonth, endOfMonth, subMonths, startOfYear, startOfQuarter, endOfQuarter, addDays } from 'date-fns';
import { StatTile } from '../StatTile';
import DataTable from '../DataTable';
import { PAYERS } from '../../lib/serviceModel';
import { explainEobs, indexEobCodes } from '../../lib/edi/eobCodes';
import {
  RECEIVABLE_STATUS, MANUAL_STATUSES, DME_CLAIMED_STATUSES,
  lineToReceivable, dmeToReceivable, isOutstanding, isUnfiled, ageReceivables, daysOutstanding, statusInfo,
} from '../../lib/receivables';

const PERIOD_OPTIONS = ['All Time', 'This Month', 'Last Month', 'This Quarter', 'This Year'];
const STATUS_KEYS = Object.keys(RECEIVABLE_STATUS);

// Appeal window by payer key, in days from filing. GAMMIS and the CMOs give
// 90; the rest are AHFC's own follow-up horizon rather than a payer rule.
const APPEAL_WINDOWS = { gapp: 90, medicaid: 90, caresource: 90, private_insurance: 60, self_pay: 30 };

const today = () => format(new Date(), 'yyyy-MM-dd');
const EMPTY_FORM = {
  client_uuid: '', client_name: '', medicaid_id: '', payer: 'gapp',
  service_code: '', service_date: '', hours: '', rate: '', amount: '',
  icn: '', status: 'submitted', submitted_date: today(), paid_date: '', paid_amount: '',
  denial_code: '', denial_reason: '', notes: '',
};

const fmt$  = (n) => '$' + (n || 0).toLocaleString('en-US', { maximumFractionDigits: 0 });
const fmt$2 = (n) => '$' + (n || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const fmtPct = (n) => isFinite(n) ? n.toFixed(1) + '%' : '—';
const fmtDay = (d) => d ? format(new Date(d + 'T12:00:00'), 'MMM d, yyyy') : '—';
const pillStyle = { fontSize: 10, fontWeight: 600, padding: '2px 7px', borderRadius: 20, whiteSpace: 'nowrap' };

export default function Receivables() {
  const navigate = useNavigate();
  const [rows,      setRows]      = useState([]);
  const [clients,   setClients]   = useState([]);
  const [loading,   setLoading]   = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editing,   setEditing]   = useState(null);   // line id
  const [form,      setForm]      = useState(EMPTY_FORM);
  const [saveError, setSaveError] = useState('');
  const [statusFilter, setStatusFilter] = useState('All');
  const [periodFilter, setPeriodFilter] = useState('This Month');
  const [search,    setSearch]    = useState('');
  const [sortKey,   setSortKey]   = useState('submitted_date');
  const [sortAsc,   setSortAsc]   = useState(false);
  const [deleteId,  setDeleteId]  = useState(null);

  // Denial codes explained in the words GAMMIS prints on the remittance.
  const [eobIndex, setEobIndex] = useState({ eob: {}, carc: {}, rarc: {} });
  useEffect(() => {
    supabase.from('eob_codes').select('kind, code, description')
      .then(({ data }) => setEobIndex(indexEobCodes(data || [])));
  }, []);
  const explain = (codes) => explainEobs(codes, eobIndex).filter(e => e.description);

  useEffect(() => { load(); }, []);

  const load = async () => {
    const [linesRes, runsRes, clientsRes, dmeRes] = await Promise.all([
      supabase.from('claim_lines').select('*').order('service_date', { ascending: false }),
      supabase.from('billing_runs').select('id, payer, submitted_at'),
      supabase.from('clients').select('id, client_id, first_name, last_name, medicaid_id, service_type').order('last_name'),
      supabase.from('dme_orders').select('*').in('status', DME_CLAIMED_STATUSES).order('claim_submitted_date', { ascending: false }),
    ]);
    const runById = Object.fromEntries((runsRes.data || []).map(r => [r.id, r]));
    const clientById = Object.fromEntries((clientsRes.data || []).map(c => [c.id, c]));
    const hh  = (linesRes.data || []).map(l => lineToReceivable(l, runById[l.billing_run_id]));
    const dme = (dmeRes.data || []).map(o => dmeToReceivable({ ...o, client: clientById[o.client_id] }));
    setRows([...hh, ...dme]);
    setClients((clientsRes.data || []).filter(c => c.client_id));
    setLoading(false);
  };

  // ── Period ───────────────────────────────────────────────────────────────
  const periodRange = (p) => {
    const now = new Date();
    if (p === 'This Month')   return { start: startOfMonth(now), end: now };
    if (p === 'Last Month')   { const lm = subMonths(now, 1); return { start: startOfMonth(lm), end: endOfMonth(lm) }; }
    if (p === 'This Quarter') return { start: startOfQuarter(now), end: endOfQuarter(now) };
    if (p === 'This Year')    return { start: startOfYear(now), end: now };
    return null;
  };
  const inPeriod = (r) => {
    const range = periodRange(periodFilter);
    if (!range) return true;
    // Unfiled lines are placed by service date; everything else by filing.
    const d = isUnfiled(r) ? r.service_date : r.submitted_date;
    if (!d) return false;
    const dt = new Date(d + 'T12:00:00');
    return dt >= range.start && dt <= range.end;
  };

  // ── Manual entry ─────────────────────────────────────────────────────────
  const set = (k) => (e) => {
    const val = e.target.value;
    setForm(p => {
      const next = { ...p, [k]: val };
      if ((k === 'hours' || k === 'rate') && next.hours && next.rate) {
        next.amount = (parseFloat(next.hours) * parseFloat(next.rate)).toFixed(2);
      }
      return next;
    });
  };
  const handleClientSelect = (e) => {
    const c = clients.find(x => x.id === e.target.value);
    if (c) setForm(p => ({ ...p, client_uuid: c.id, client_name: `${c.first_name} ${c.last_name}`.trim(), medicaid_id: c.medicaid_id || '', payer: c.service_type || p.payer }));
  };
  const openNew = () => { setEditing(null); setForm({ ...EMPTY_FORM, submitted_date: today() }); setSaveError(''); setShowModal(true); };
  const openEdit = (r) => {
    if (r._source !== 'manual') return;
    setEditing(r.line_id);
    setForm({
      ...EMPTY_FORM,
      client_uuid: r.client_id || '', client_name: r.client_name || '', medicaid_id: r.medicaid_id || '',
      payer: r.payer_key || 'gapp', service_code: r.service_code || '', service_date: r.service_date || '',
      hours: r.hours ?? '', rate: r.hours && r.amount ? (r.amount / r.hours).toFixed(2) : '', amount: r.amount ?? '',
      icn: r.icn || '', status: r.status, submitted_date: r.submitted_date || '', paid_date: r.paid_date || '',
      paid_amount: r.paid_amount ?? '', denial_code: r.denial_code || '', denial_reason: r.denial_reason || '', notes: r.notes || '',
    });
    setSaveError(''); setShowModal(true);
  };

  const handleSave = async () => {
    if (!form.client_name || !form.amount || !form.service_date) return;
    const amount = parseFloat(form.amount);
    const payload = {
      source: 'manual', billing_run_id: null,
      client_id: form.client_uuid || null, client_name: form.client_name, medicaid_id: form.medicaid_id || '',
      payer: form.payer || null,
      service_code: form.service_code || null, service_date: form.service_date,
      hours: form.hours ? parseFloat(form.hours) : null,
      rate:  form.rate  ? parseFloat(form.rate)  : null,
      charge_amount: amount, accrued_amount: amount, gross_charge_amount: amount,
      icn: form.icn || null,
      status: form.status,
      submitted_at: form.submitted_date ? `${form.submitted_date}T12:00:00Z` : null,
      paid_date: form.status === 'paid' ? (form.paid_date || null) : null,
      paid_amount: form.status === 'paid' ? (form.paid_amount ? parseFloat(form.paid_amount) : amount) : null,
      denial_code: form.status === 'denied' ? (form.denial_code || null) : null,
      denial_reason: form.status === 'denied' ? (form.denial_reason || null) : null,
      notes: form.notes || null,
      updated_at: new Date().toISOString(),
    };
    const { error } = editing
      ? await supabase.from('claim_lines').update(payload).eq('id', editing).eq('source', 'manual')
      : await supabase.from('claim_lines').insert([payload]);
    if (error) { setSaveError(error.message); return; }
    setShowModal(false);
    load();
  };

  const handleStatus = async (r, status) => {
    if (r._source !== 'manual') return;
    const update = { status, updated_at: new Date().toISOString() };
    if (status === 'paid' && !r.paid_date) { update.paid_date = today(); update.paid_amount = r.paid_amount ?? r.amount; }
    const { error } = await supabase.from('claim_lines').update(update).eq('id', r.line_id).eq('source', 'manual');
    if (error) { window.alert(error.message); return; }
    setRows(prev => prev.map(x => x.id === r.id ? { ...x, ...update } : x));
  };

  const handleDelete = async () => {
    const r = rows.find(x => x.id === deleteId);
    if (!r || r._source !== 'manual') return;
    const { error } = await supabase.from('claim_lines').delete().eq('id', r.line_id).eq('source', 'manual');
    if (error) { window.alert(error.message); return; }
    setRows(prev => prev.filter(x => x.id !== deleteId));
    setDeleteId(null);
  };

  const sorting = useMemo(() => sortKey ? [{ id: sortKey, desc: !sortAsc }] : [], [sortKey, sortAsc]);
  const handleSortingChange = (updaterOrValue) => {
    const next = typeof updaterOrValue === 'function' ? updaterOrValue(sorting) : updaterOrValue;
    if (!next.length) return;
    setSortKey(next[0].id);
    setSortAsc(!next[0].desc);
  };

  const exportCSV = () => {
    const header = ['Type', 'Client', 'Medicaid ID', 'Payer', 'Service', 'Service Date', 'Hours', 'Amount', 'Paid', 'Status', 'Filed', 'Paid Date', 'Claim No. / ICN', 'Denial Code', 'Denial Reason', 'Notes'];
    const body = filtered.map(r => [r._source.toUpperCase(), r.client_name, r.medicaid_id, r.payer, r.service_code, r.service_date, r.hours ?? '', r.amount, r.paid_amount ?? '', statusInfo(r.status).label, r.submitted_date || '', r.paid_date || '', r.icn || '', r.denial_code || '', r.denial_reason || '', r.notes || '']);
    const csv = [header, ...body].map(row => row.map(v => `"${String(v ?? '').replace(/"/g, '""')}"`).join(',')).join('\n');
    const a = document.createElement('a');
    a.href = 'data:text/csv,' + encodeURIComponent(csv);
    a.download = `receivables_${format(new Date(), 'yyyyMMdd')}.csv`;
    a.click();
  };

  // ── KPIs ─────────────────────────────────────────────────────────────────
  const now = new Date();
  const periodRows = rows.filter(inPeriod);
  const sum = (list) => list.reduce((s, r) => s + (r.amount || 0), 0);
  const totals = {
    count:       periodRows.length,
    unfiled:     sum(periodRows.filter(isUnfiled)),
    unfiledCt:   periodRows.filter(isUnfiled).length,
    outstanding: sum(periodRows.filter(isOutstanding)),
    paid:        periodRows.filter(r => r.status === 'paid').reduce((s, r) => s + (r.paid_amount ?? r.amount ?? 0), 0),
    paidCt:      periodRows.filter(r => r.status === 'paid').length,
    denied:      sum(periodRows.filter(r => r.status === 'denied')),
    deniedCt:    periodRows.filter(r => r.status === 'denied').length,
    filedCt:     periodRows.filter(r => !isUnfiled(r) && r.status !== 'voided').length,
  };
  const denialRate     = totals.filedCt > 0 ? (totals.deniedCt / totals.filedCt) * 100 : 0;
  const collectionRate = (totals.paid + totals.outstanding) > 0 ? (totals.paid / (totals.paid + totals.outstanding)) * 100 : 0;
  const paidWithDates  = periodRows.filter(r => r.status === 'paid' && r.submitted_date && r.paid_date);
  const avgDaysToPay   = paidWithDates.length
    ? paidWithDates.reduce((s, r) => s + Math.max(0, (new Date(r.paid_date) - new Date(r.submitted_date)) / 86400000), 0) / paidWithDates.length
    : null;

  // AR aging — everything outstanding, whatever the period filter says.
  const aging = ageReceivables(rows, now);
  const agingTotal = aging.a + aging.b + aging.c + aging.d;
  const outstandingCt = aging.aCt + aging.bCt + aging.cCt + aging.dCt;

  const denialByCode = {};
  periodRows.filter(r => r.status === 'denied').forEach(r => {
    const code = r.denial_code || 'Unspecified';
    if (!denialByCode[code]) denialByCode[code] = { count: 0, amount: 0, label: explain(code)[0]?.description || null };
    denialByCode[code].count++;
    denialByCode[code].amount += r.amount || 0;
  });

  const filtered = periodRows
    .filter(r => statusFilter === 'All' || r.status === statusFilter)
    .filter(r => !search || r.client_name?.toLowerCase().includes(search.toLowerCase()) || r.medicaid_id?.includes(search) || r.icn?.includes(search))
    .sort((a, b) => {
      const av = a[sortKey] ?? '', bv = b[sortKey] ?? '';
      const cmp = String(av).localeCompare(String(bv), undefined, { numeric: true });
      return sortAsc ? cmp : -cmp;
    });

  const tableRows = filtered.map(r => {
    const daysToPayment = r.submitted_date && r.paid_date
      ? Math.max(0, Math.round((new Date(r.paid_date) - new Date(r.submitted_date)) / 86400000)) : null;
    const daysSince = daysOutstanding(r, now);
    const stale = isOutstanding(r) && daysSince > 60;
    let appealDeadline = null, daysToAppeal = null;
    if (r.status === 'denied' && r.submitted_date) {
      appealDeadline = addDays(new Date(r.submitted_date + 'T12:00:00'), APPEAL_WINDOWS[r.payer_key] || 60);
      daysToAppeal = Math.floor((appealDeadline - now) / 86400000);
    }
    return { ...r, _daysToPayment: daysToPayment, _daysSince: daysSince, _stale: stale, _appealDeadline: appealDeadline, _daysToAppeal: daysToAppeal };
  });

  const TYPE_LABEL = { hh: 'HH', dme: 'DME', manual: 'Manual' };

  const columns = [
    {
      id: 'type', header: 'Type', size: 64, enableSorting: false, meta: { mobile: 'secondary' },
      cell: info => <span style={{ ...pillStyle, background: 'var(--bg2)', color: 'var(--text3)' }}>{TYPE_LABEL[info.row.original._source]}</span>,
    },
    {
      id: 'client_name', accessorKey: 'client_name', header: 'Client', size: 170, meta: { mobile: 'primary' },
      cell: info => {
        const r = info.row.original;
        return (
          <div>
            <div style={{ fontWeight: 500 }}>{r.client_name}</div>
            {r.order_ref && <div style={{ fontSize: 10, fontFamily: 'monospace', color: 'var(--text3)' }}>{r.order_ref}</div>}
            {r.icn && <div style={{ fontSize: 10, fontFamily: 'monospace', color: 'var(--text3)' }}>{r.icn}</div>}
          </div>
        );
      },
    },
    { id: 'medicaid_id', accessorKey: 'medicaid_id', header: 'Medicaid ID', size: 110, meta: { mono: true, mobile: 'secondary' }, cell: info => info.getValue() || '—' },
    { id: 'payer', accessorKey: 'payer', header: 'Payer', size: 100, meta: { mobile: 'hidden' }, cell: info => <span style={{ fontSize: 11, color: 'var(--text2)' }}>{info.getValue()}</span> },
    {
      id: 'service_code', accessorKey: 'service_code', header: 'Service', size: 110, meta: { mobile: 'hidden' },
      cell: info => {
        const r = info.row.original;
        const codes = r.eobs_codes || r.denial_code;
        return (
          <span style={{ fontSize: 11, color: 'var(--text3)' }}>
            {r.service_code || '—'}
            {r.status === 'denied' && (r.denial_code || r.denial_reason) && (
              <div style={{ fontSize: 10, color: 'var(--danger)', fontWeight: 500, marginTop: 1 }} title={explain(codes).map(e => e.text).join('\n') || undefined}>
                {r.denial_code || 'Denied'}
                {(explain(codes)[0]?.description || r.denial_reason) && (
                  <div style={{ fontWeight: 400, color: 'var(--text3)', lineHeight: 1.4, marginTop: 1 }}>
                    {explain(codes)[0]?.description || r.denial_reason}
                  </div>
                )}
              </div>
            )}
          </span>
        );
      },
    },
    { id: 'service_date', accessorKey: 'service_date', header: 'Service Date', size: 105, meta: { mobile: 'hidden' }, cell: info => fmtDay(info.getValue()) },
    { id: 'hours', accessorKey: 'hours', header: 'Hours', size: 65, meta: { align: 'right', mobile: 'hidden' }, cell: info => info.getValue() ? `${info.getValue()}h` : '—' },
    { id: 'amount', accessorKey: 'amount', header: 'Amount', size: 95, meta: { align: 'right', mobile: 'primary' }, cell: info => <span style={{ fontWeight: 500 }}>{fmt$2(info.getValue())}</span> },
    {
      id: 'submitted_date', accessorKey: 'submitted_date', header: 'Filed', size: 125, meta: { mobile: 'secondary' },
      cell: info => {
        const r = info.row.original;
        if (isUnfiled(r)) return <span style={{ color: 'var(--text3)' }}>—</span>;
        return (
          <span style={{ color: r._stale ? 'var(--danger)' : undefined }}>
            {fmtDay(r.submitted_date)}
            {r._stale && <div style={{ fontSize: 9, color: 'var(--danger)' }}>{r._daysSince}d outstanding</div>}
          </span>
        );
      },
    },
    {
      id: 'paid_date', accessorKey: 'paid_date', header: 'Paid', size: 150, meta: { mobile: 'secondary' },
      cell: info => {
        const r = info.row.original;
        if (r.paid_date) {
          return (
            <span style={{ color: 'var(--success)' }}>
              {r.paid_amount != null ? fmt$2(r.paid_amount) + ' · ' : ''}{fmtDay(r.paid_date)}
              {r._daysToPayment != null && <span style={{ color: 'var(--text3)', marginLeft: 4 }}>({r._daysToPayment}d)</span>}
            </span>
          );
        }
        if (r._appealDeadline) {
          return (
            <div>
              <span style={{ fontSize: 10, fontWeight: 600, color: r._daysToAppeal < 14 ? 'var(--danger)' : r._daysToAppeal < 30 ? 'var(--warn)' : 'var(--text3)' }}>
                Appeal by {format(r._appealDeadline, 'MMM d, yyyy')}
              </span>
              <div style={{ fontSize: 9, color: r._daysToAppeal < 0 ? 'var(--danger)' : 'var(--text3)' }}>
                {r._daysToAppeal < 0 ? '⚠ Deadline passed' : `${r._daysToAppeal}d remaining`}
              </div>
            </div>
          );
        }
        return '—';
      },
    },
    {
      id: 'status', accessorKey: 'status', header: 'Status', size: 130, meta: { mobile: 'primary' },
      cell: info => {
        const r = info.row.original;
        const pill = statusInfo(r.status);
        if (r._source === 'manual') {
          return (
            <select value={r.status} onClick={e => e.stopPropagation()} onChange={e => handleStatus(r, e.target.value)}
              style={{ fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 20, border: 'none', cursor: 'pointer', fontFamily: 'inherit', background: pill.bg, color: pill.color }}>
              {MANUAL_STATUSES.map(s => <option key={s} value={s}>{RECEIVABLE_STATUS[s].label}</option>)}
            </select>
          );
        }
        return <span style={{ fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 20, background: pill.bg, color: pill.color }}>{pill.label}</span>;
      },
    },
    {
      id: 'actions', header: '', size: 110, enableSorting: false, meta: { mobile: 'hidden' },
      cell: info => {
        const r = info.row.original;
        if (r._source !== 'manual') {
          return (
            <button className="btn btn-ghost btn-sm" style={{ fontSize: 11 }}
              onClick={e => { e.stopPropagation(); navigate(r._source === 'hh' ? '/finance/claims?tab=runs' : '/dme/orders'); }}>
              View →
            </button>
          );
        }
        return (
          <div style={{ display: 'flex', gap: 4 }} onClick={e => e.stopPropagation()}>
            <button className="btn btn-ghost btn-sm" style={{ fontSize: 11 }} onClick={() => openEdit(r)}>Edit</button>
            {deleteId === r.id ? (
              <span style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
                <button className="btn btn-sm" style={{ fontSize: 11, background: 'var(--danger)', color: '#fff', border: 'none' }} onClick={handleDelete}>Confirm</button>
                <button className="btn btn-ghost btn-sm" style={{ fontSize: 11 }} onClick={() => setDeleteId(null)}>✕</button>
              </span>
            ) : (
              <button className="btn btn-ghost btn-sm" style={{ fontSize: 11, color: 'var(--danger)' }} onClick={() => setDeleteId(r.id)}>Del</button>
            )}
          </div>
        );
      },
    },
  ];

  const pillBtn = (active) => ({ padding: '3px 10px', borderRadius: 20, fontSize: 11, fontWeight: 500, cursor: 'pointer', fontFamily: 'inherit', border: '0.5px solid', background: active ? 'var(--accent)' : 'transparent', color: active ? '#fff' : 'var(--text3)', borderColor: active ? 'var(--accent)' : 'var(--border)' });

  return (
    <div>
      <div style={{ display: 'flex', gap: 6, marginBottom: 14, flexWrap: 'wrap', alignItems: 'center' }}>
        <span style={{ fontSize: 12, color: 'var(--text3)', fontWeight: 500 }}>Period:</span>
        {PERIOD_OPTIONS.map(p => (
          <button key={p} onClick={() => setPeriodFilter(p)} style={{ ...pillBtn(periodFilter === p), padding: '4px 12px', fontSize: 12 }}>{p}</button>
        ))}
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 8, marginBottom: 8 }}>
        {[
          { label: 'Claims',       value: totals.count,             tone: 'neutral' },
          { label: 'Not filed',    value: fmt$(totals.unfiled),     tone: 'neutral' },
          { label: 'Outstanding',  value: fmt$(totals.outstanding), tone: totals.outstanding > 0 ? 'warn' : 'neutral' },
          { label: 'Paid',         value: fmt$(totals.paid),        tone: 'neutral' },
          { label: 'Denied',       value: fmt$(totals.denied),      tone: totals.denied > 0 ? 'alert' : 'neutral' },
        ].map(s => <StatTile key={s.label} label={s.label} value={s.value} tone={s.tone} />)}
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 8, marginBottom: 14 }}>
        {[
          { label: 'Denial Rate',         value: fmtPct(denialRate),     sub: 'of filed · target <10%',   warn: denialRate > 10,     good: denialRate <= 10 },
          { label: 'Collection Rate',     value: fmtPct(collectionRate), sub: 'Target ≥90%',              warn: collectionRate < 75, good: collectionRate >= 90 },
          { label: 'Avg Days to Payment', value: avgDaysToPay != null ? `${avgDaysToPay.toFixed(0)}d` : '—', sub: 'Filed → paid', warn: avgDaysToPay > 60, good: avgDaysToPay != null && avgDaysToPay <= 30 },
          { label: 'Claims Paid',         value: totals.paidCt,          sub: `of ${totals.filedCt} filed`, good: true },
        ].map(s => (
          <div key={s.label} style={{ background: 'var(--bg2)', border: '0.5px solid var(--border)', borderRadius: 10, padding: '14px 16px' }}>
            <div style={{ fontSize: 9, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.08em', color: 'var(--text3)', marginBottom: 6 }}>{s.label}</div>
            <div style={{ fontSize: 22, fontWeight: 500, lineHeight: 1, color: s.warn ? 'var(--danger)' : s.good ? 'var(--success)' : 'var(--text2)' }}>{s.value}</div>
            <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 4 }}>{s.sub}</div>
          </div>
        ))}
      </div>

      {/* AR aging — filed and still open, from the filing date */}
      <div className="card" style={{ marginBottom: 14 }}>
        <div className="card-header">
          <div className="card-title">AR Aging — Filed and Outstanding</div>
          <span style={{ fontSize: 11, color: 'var(--text3)' }}>{outstandingCt} claims · {fmt$(agingTotal)} total</span>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 0 }}>
          {[
            { label: '0–30 days',  value: aging.a, count: aging.aCt, color: 'var(--success)', bg: 'var(--success-bg)' },
            { label: '31–60 days', value: aging.b, count: aging.bCt, color: 'var(--warn)',    bg: 'var(--warn-bg)' },
            { label: '61–90 days', value: aging.c, count: aging.cCt, color: 'var(--warn)',    bg: 'var(--warn-bg)' },
            { label: '90+ days',   value: aging.d, count: aging.dCt, color: 'var(--danger)',  bg: 'var(--danger-bg)' },
          ].map((b, i) => (
            <div key={b.label} style={{ padding: '14px 18px', borderRight: i < 3 ? '0.5px solid var(--border)' : 'none', background: b.value > 0 ? b.bg : 'transparent' }}>
              <div style={{ fontSize: 10, fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.06em', color: b.color, marginBottom: 4 }}>{b.label}</div>
              <div style={{ fontSize: 20, fontWeight: 500, color: b.value > 0 ? b.color : 'var(--text3)' }}>{fmt$(b.value)}</div>
              <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 2 }}>{b.count} claim{b.count !== 1 ? 's' : ''}</div>
              <div style={{ marginTop: 6, height: 3, borderRadius: 2, background: 'var(--border)' }}>
                <div style={{ height: 3, borderRadius: 2, background: b.color, width: agingTotal > 0 ? `${Math.min((b.value / agingTotal) * 100, 100)}%` : '0%' }} />
              </div>
            </div>
          ))}
        </div>
        {aging.dCt > 0 && (
          <div style={{ padding: '10px 18px', borderTop: '0.5px solid var(--border)', fontSize: 12, color: 'var(--danger)', background: 'var(--danger-bg)' }}>
            ⚠ {aging.dCt} claim{aging.dCt > 1 ? 's are' : ' is'} over 90 days outstanding ({fmt$(aging.d)}). Review for write-off or escalation.
          </div>
        )}
        {totals.unfiledCt > 0 && (
          <div style={{ padding: '8px 18px', borderTop: '0.5px solid var(--border)', fontSize: 11, color: 'var(--text3)' }}>
            {totals.unfiledCt} line{totals.unfiledCt === 1 ? '' : 's'} in this period ({fmt$(totals.unfiled)}) built and not yet filed. Not aged — a run is not a receivable until it goes out.
          </div>
        )}
      </div>

      {Object.keys(denialByCode).length > 0 && (
        <div className="card" style={{ marginBottom: 14 }}>
          <div className="card-header">
            <div className="card-title">Denials by Code</div>
            <span style={{ fontSize: 11, color: 'var(--text3)' }}>{totals.deniedCt} this period</span>
          </div>
          <div className="table-wrapper">
            <table>
              <thead><tr>
                {['Code', 'Claims', 'Amount denied', '% of denials'].map(h => <th key={h} style={{ color: 'var(--text3)' }}>{h}</th>)}
              </tr></thead>
              <tbody>
                {Object.entries(denialByCode).sort((a, b) => b[1].count - a[1].count).map(([code, d]) => (
                  <tr key={code}>
                    <td style={{ fontWeight: 500 }}>
                      {code}
                      {d.label && <div style={{ fontWeight: 400, fontSize: 11, color: 'var(--text3)', lineHeight: 1.45, marginTop: 2, maxWidth: 380 }}>{d.label}</div>}
                    </td>
                    <td>{d.count}</td>
                    <td style={{ color: 'var(--danger)', fontWeight: 500 }}>{fmt$2(d.amount)}</td>
                    <td style={{ color: 'var(--text3)', fontSize: 12 }}>{totals.deniedCt > 0 ? ((d.count / totals.deniedCt) * 100).toFixed(0) + '%' : '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <div className="card">
        <div className="card-header">
          <div className="card-title">All Claims</div>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
            <input placeholder="Search client, Medicaid ID, claim no…" value={search} onChange={e => setSearch(e.target.value)}
              style={{ border: '0.5px solid var(--border)', borderRadius: 6, padding: '5px 10px', fontSize: 12, fontFamily: 'inherit', background: 'var(--bg)', color: 'var(--text)', minWidth: 180, outline: 'none' }} />
            <button className="btn btn-outline btn-sm" onClick={exportCSV}>Export CSV</button>
            <button className="btn btn-primary btn-sm" onClick={openNew} title="A claim keyed into a portal, sent on paper, or filed before CAERA">+ Record a claim filed outside CAERA</button>
          </div>
        </div>

        <div style={{ display: 'flex', gap: 6, padding: '0 16px 12px', borderBottom: '0.5px solid var(--border)', flexWrap: 'wrap' }}>
          {['All', ...STATUS_KEYS].map(f => (
            <button key={f} onClick={() => setStatusFilter(f)} style={pillBtn(statusFilter === f)}>
              {f === 'All' ? 'All' : RECEIVABLE_STATUS[f].label} ({f === 'All' ? periodRows.length : periodRows.filter(r => r.status === f).length})
            </button>
          ))}
        </div>

        <DataTable
          tableId="receivables"
          columns={columns}
          data={tableRows}
          getRowId={r => r.id}
          sorting={sorting}
          onSortingChange={handleSortingChange}
          manualSorting
          loading={loading}
          emptyMessage={rows.length === 0 ? 'No claims yet.' : 'No claims match this filter.'}
          maxHeight="calc(100vh - 260px)"
          rowStyle={r => r._stale ? { background: 'var(--danger-bg)' } : null}
        />
      </div>

      {showModal && (
        <div className="modal-overlay" onMouseDown={e => e.target === e.currentTarget && setShowModal(false)}>
          <div className="modal" style={{ maxWidth: 640 }} onMouseDown={e => e.stopPropagation()}>
            <div className="modal-header">
              <div>
                <div className="modal-title">{editing ? 'Edit recorded claim' : 'Record a claim filed outside CAERA'}</div>
                <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 2 }}>
                  For a claim keyed into a payer portal, sent on paper, or filed before CAERA. Runs built here are not entered this way.
                </div>
              </div>
              <button className="btn btn-ghost btn-sm" onClick={() => setShowModal(false)}>✕</button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label className="form-label">Client</label>
                <select className="form-select" onChange={handleClientSelect} value={form.client_uuid}>
                  <option value="">— Select client —</option>
                  {clients.map(c => <option key={c.id} value={c.id}>{c.last_name}, {c.first_name} ({c.client_id})</option>)}
                </select>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Client name <span className="req">*</span></label>
                  <input className="form-input" value={form.client_name} onChange={set('client_name')} />
                </div>
                <div className="form-group">
                  <label className="form-label">Medicaid ID</label>
                  <input className="form-input" value={form.medicaid_id} onChange={set('medicaid_id')} />
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Payer</label>
                  <select className="form-select" value={form.payer} onChange={set('payer')}>
                    {PAYERS.map(p => <option key={p.key} value={p.key}>{p.label}</option>)}
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label">Service code</label>
                  <input className="form-input" value={form.service_code} onChange={set('service_code')} placeholder="e.g. S9123" />
                </div>
                <div className="form-group">
                  <label className="form-label">Service date <span className="req">*</span></label>
                  <input type="date" className="form-input" value={form.service_date} onChange={set('service_date')} />
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Hours</label>
                  <input type="number" className="form-input" value={form.hours} onChange={set('hours')} step="0.25" />
                </div>
                <div className="form-group">
                  <label className="form-label">Rate/hr ($)</label>
                  <input type="number" className="form-input" value={form.rate} onChange={set('rate')} step="0.01" />
                </div>
                <div className="form-group">
                  <label className="form-label">Amount billed ($) <span className="req">*</span></label>
                  <input type="number" className="form-input" value={form.amount} onChange={set('amount')} step="0.01" />
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Claim number / ICN</label>
                  <input className="form-input" value={form.icn} onChange={set('icn')} />
                </div>
                <div className="form-group">
                  <label className="form-label">Status</label>
                  <select className="form-select" value={form.status} onChange={set('status')}>
                    {MANUAL_STATUSES.map(s => <option key={s} value={s}>{RECEIVABLE_STATUS[s].label}</option>)}
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label">Filed on</label>
                  <input type="date" className="form-input" value={form.submitted_date} onChange={set('submitted_date')} />
                </div>
              </div>
              {form.status === 'paid' && (
                <div className="form-row">
                  <div className="form-group">
                    <label className="form-label">Paid on</label>
                    <input type="date" className="form-input" value={form.paid_date} onChange={set('paid_date')} />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Amount paid ($)</label>
                    <input type="number" className="form-input" value={form.paid_amount} onChange={set('paid_amount')} step="0.01" placeholder={form.amount || ''} />
                  </div>
                </div>
              )}
              {form.status === 'denied' && (
                <div className="form-row">
                  <div className="form-group">
                    <label className="form-label">Denial code</label>
                    <input className="form-input" value={form.denial_code} onChange={set('denial_code')} placeholder="EOB / CARC as printed" />
                    {form.denial_code && explain(form.denial_code)[0] && (
                      <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 4 }}>{explain(form.denial_code)[0].description}</div>
                    )}
                  </div>
                  <div className="form-group">
                    <label className="form-label">Reason as given</label>
                    <input className="form-input" value={form.denial_reason} onChange={set('denial_reason')} />
                  </div>
                </div>
              )}
              <div className="form-group">
                <label className="form-label">Notes</label>
                <textarea className="form-textarea" rows={2} value={form.notes} onChange={set('notes')} placeholder="Where it was filed, reference numbers, follow-up…" />
              </div>
              {saveError && <div style={{ fontSize: 12, color: 'var(--danger)' }}>{saveError}</div>}
            </div>
            <div className="modal-footer">
              <button className="btn btn-outline" onClick={() => setShowModal(false)}>Cancel</button>
              <button className="btn btn-primary" onClick={handleSave} disabled={!form.client_name || !form.amount || !form.service_date}>
                {editing ? 'Save changes' : 'Record claim'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
